import React, { useRef } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Iconify from '@/components/Iconify';
import { usePromotionForm } from '@/sections/promotion';
import { fCurrency } from '@/utils/formatNumber';
import useEventContext from '@/contexts/event/useEventContext';
import useTicketsContext from '@/contexts/tickets/useTicketsContext';
import PromotionBanner from '@/sections/promotion/PromotionBanner';
import PromotionButton from '@/sections/promotion/PromotionButton';
import CollapseBoxHtml from '@/templates/plus/components/CollapseBoxHtml';
import useBoletiaConfig from '@/hooks/useBoletiaConfig';
import useMobile from '@/hooks/useMobile';
import RenderIf from '@/components/RenderIf';

function TicketSummary({ hidePromo = false }) {
  const { t } = useTranslation();
  const { isUserToViewAvailability, isEventToViewAvailability } =
    useBoletiaConfig();
  const { promotions, getItemDiscount } = usePromotionForm();
  const {
    event: {
      event: { user_id, id: eventID },
      preferences: { fee_breakdown, currency },
    },
  } = useEventContext();

  const { isMobile } = useMobile();

  const {
    ticketSelected: {
      ticket_type_id,
      name,
      availability,
      max_price,
      description,
      category_name,
    } = { ticket_type_id: '', description: '' },
  } = useTicketsContext();

  const isDesc = !!description.replace(/<[^>]+>/g, '').replace(/ /g, '');

  return (
    <>
      {!hidePromo && (
        <Box
          sx={{
            'width': '100%',
            'mt': 2,
            'mb': 2,
            '& .MuiButtonBase-root': {
              zIndex: 0,
            },
          }}
        >
          <Stack alignItems="center" direction="row">
            <Box sx={{ flex: 1 }}></Box>
            <PromotionButton rounded />
          </Stack>
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          mt: 2,
        }}
      >
        <PromotionBanner />
      </Box>
      <Box
        sx={{
          width: '100%',
          mt: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" component="div">
            {name}
          </Typography>

          <RenderIf condition={isMobile}>
            <PromotionButton rounded />
          </RenderIf>
        </Stack>
        {(isEventToViewAvailability(eventID) ||
          isUserToViewAvailability(user_id)) && (
          <Typography variant="body5" component="span">
            {`${category_name}: `}
            <Typography
              variant="body5"
              component="span"
              sx={{ color: (theme) => theme.palette.blue }}
            >
              {t('checkout.home.cart.ticketsAvailability', { availability })}
            </Typography>
          </Typography>
        )}
      </Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          pr: 1,
          mt: 1,
          mb: 1,
        }}
      >
        <Iconify width={20} height={20} icon={'la:money-bill'} />
        {promotions && (
          <Box>
            <Typography
              variant="body3"
              component="span"
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              {t('checkout.home.cart.price')}
            </Typography>
            <Typography
              variant="body2"
              component="span"
              sx={{
                color: '#139e65',
              }}
            >
              {`${fCurrency(
                getItemDiscount(max_price, ticket_type_id),
              )} ${currency} `}
            </Typography>
            <Typography
              variant="body3"
              component="span"
              sx={{
                color: (theme) => theme.palette.grey[500],
                textDecoration: 'line-through',
              }}
            >
              {`${fCurrency(max_price)} ${currency}`}
            </Typography>
            <Typography
              variant="body3"
              component="span"
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              {fee_breakdown === 'f' ? '' : t('checkout.home.cart.plusCharge')}
            </Typography>
          </Box>
        )}
        {!promotions && (
          <Typography
            variant="body3"
            component="div"
            sx={{ minWidth: '155px', textAlign: 'right' }}
          >
            {`${t('checkout.home.cart.price')}${fCurrency(
              max_price,
            )} ${currency}`}
            <Typography
              variant="body3"
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              {fee_breakdown === 'f' ? '' : t('checkout.home.cart.plusCharge')}
            </Typography>
          </Typography>
        )}
      </Stack>
      <Box
        sx={{
          width: '100%',
          mb: 1,
        }}
      >
        {isDesc && (
          <CollapseBoxHtml html={description} minsize={26} starClose />
        )}
        {!isDesc && (
          <Typography variant="caption2" component="div">
            {category_name}
          </Typography>
        )}
      </Box>
    </>
  );
}

export default TicketSummary;
